// libraries
import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Form, Input, Select, Button, Switch, notification, Typography, FormInstance } from 'antd';
import { CheckOutlined, DeleteFilled, WarningOutlined } from '@ant-design/icons';
import * as Colors from '@ant-design/colors';

// components
import { DeleteModal } from '../../../../shared/delete-modal';
import { SendWarningModal } from '../../../../shared/send-warning-modal';

// services
import * as UserService from '../../../../../services/bb-api/user';
import * as MunicipalityService from '../../../../../services/bb-api/municipality';
import * as UniverseService from '../../../../../services/bb-api/universe';

import { txt } from '../../../../../locales/i18n';
import { useDeviceSize } from '../../../../../hooks/device-size';

function handleSelectionFilter(input: string, option: any): boolean {
	const target: string = option.children.toLowerCase();
	return target.startsWith(input.toLowerCase());
}

const aauucnId = 1;
const universities = ['aau', 'ucn'];

export function UserFormTab(props: any): JSX.Element {
	const [user, setUser] = props.useUser;
	const requiredRule: Record<string, boolean | string> = {
		required: true,
		message: txt('global.required'),
	};
	const isMobileDevice: boolean = useDeviceSize();

	const [updateLoading, setUpdateLoading] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showWarningMailModal, setShowWarningMailModal] = useState<boolean>(false);
	const [municipalities, setMunicipalities] = useState<Record<string, any>[]>([]);
	const [universes, setUniverses] = useState<Record<string, any>[]>([]);
	const [disablePassword, setDisablePassword] = useState<boolean>(true);
	const [rejectedHlDeletions, setRejectedHlDeletions] = useState<any[]>([]);
	const [disableUniversity, setDisableUniversity] = useState(
		user.universes.includes(aauucnId) === false
	);

	const formRef = useRef<FormInstance>(null);
	const history = useHistory();

	useEffect(() => {
		async function getFormResources(): Promise<void> {
			const municipalities: Record<string, any>[] = await MunicipalityService.query();
			const universes: Record<string, any>[] = await UniverseService.query();
			setMunicipalities(municipalities);
			setUniverses(universes);
		}

		getFormResources();
	}, []);

	async function handleUpdate(values: any): Promise<void> {
		const form = formRef.current;
		if (form === null) return;

		form?.setFields([{ name: 'university', errors: [] }]);

		if (values.universes.includes(aauucnId)) {
			const university = form.getFieldValue('university') ?? null;

			if (university === null) {
				form?.setFields([{ name: 'university', errors: [txt('global.required')] }]);
				return;
			}
		}

		setUpdateLoading(true);

		try {
			await UserService.update(user.id, values);
			notification.success({ message: txt('notification.update successful') });
			setUser({ ...user, ...values });
			history.goBack();
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setUpdateLoading(false);
	}

	async function handleDelete(): Promise<void> {
		const rejectedUris: { listId: number; memberId: string }[] | null =
			await UserService.remove(user.id);

		if (rejectedUris === null) {
			return history.goBack();
		}

		if (rejectedUris.length !== 0) {
			setRejectedHlDeletions(rejectedUris);
			throw new Error('User not deleted');
		}
	}

	function handleUniverseChange(universeIds: number[]) {
		const disableUniversity = universeIds.includes(aauucnId) === false;
		setDisableUniversity(disableUniversity);

		if (disableUniversity) {
			const form = formRef.current;
			form?.setFields([{ name: 'university', value: undefined, errors: [] }]);
		}
	}

	async function handleSendWarning(type: string) {
		try {
			await UserService.sendWarning(user.username, user.email, type);
			setShowWarningMailModal(false);
			const message = txt('user.send warning success');
			notification.success({ message });
		} catch (error) {
			const message = txt('user.send warning error');
			notification.error({ message });
		}
	}

	return (
		<StyledForm
			ref={formRef}
			onFinish={handleUpdate}
			initialValues={user}
			requiredMark={false}
			labelCol={{ span: 6 }}
			isMobileDevice={isMobileDevice}
		>
			<div className="input-container">
				<Form.Item label={txt('global.name')} name="username" rules={[requiredRule]}>
					<Input />
				</Form.Item>

				<Form.Item label="Email" name="email" rules={[requiredRule]}>
					<Input />
				</Form.Item>

				<Form.Item label={txt('global.phone')} name="phone">
					<Input />
				</Form.Item>

				<Form.Item
					label={txt('global.birth year')}
					name="birth_year"
					rules={[requiredRule]}
				>
					<Input />
				</Form.Item>

				<Form.Item label={txt('global.gender')} name="gender_id">
					<Select placeholder={txt('global.gender')} dropdownMatchSelectWidth={false}>
						<Select.Option value={0}>{txt('genders.not defined')}</Select.Option>
						<Select.Option value={1}>{txt('genders.male')}</Select.Option>
						<Select.Option value={2}>{txt('genders.female')}</Select.Option>
						<Select.Option value={3}>{txt('genders.other')}</Select.Option>
					</Select>
				</Form.Item>

				<Form.Item
					label={txt('global.municipality')}
					name="municipality_id"
					rules={[requiredRule]}
				>
					<Select
						showSearch
						dropdownMatchSelectWidth={false}
						filterOption={handleSelectionFilter}
					>
						{municipalities.map((municipality: Record<string, any>) => (
							<Select.Option key={municipality.id} value={municipality.id}>
								{municipality.name}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label={txt('global.password')}>
					<div style={{ display: 'flex', gap: '1em', alignItems: 'center' }}>
						<Form.Item noStyle>
							<Switch onChange={(value) => setDisablePassword(!value)} />
						</Form.Item>
						<Form.Item name="password" noStyle>
							<Input.Password disabled={disablePassword} />
						</Form.Item>
					</div>
				</Form.Item>

				<Form.Item label={txt('global.universe')} name="universes">
					<Select
						mode="multiple"
						placeholder="Please select"
						dropdownMatchSelectWidth={false}
						filterOption={handleSelectionFilter}
						onChange={handleUniverseChange}
					>
						{universes.map((universe: Record<string, any>) => (
							<Select.Option key={universe.id} value={universe.id}>
								{universe.title}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item name="university" label={txt('global.university')} required={true}>
					<Select
						showSearch
						allowClear
						disabled={disableUniversity}
						dropdownMatchSelectWidth={false}
					>
						{universities.map((university) => (
							<Select.Option key={university} value={university}>
								{university}
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			</div>

			<div>
				<span>{txt('global.comment')}</span>
				<Form.Item noStyle name="comment">
					<Input.TextArea autoSize />
				</Form.Item>
			</div>

			<Form.Item>
				<div className="submit-container">
					<Form.Item name="banned" valuePropName="checked">
						<Switch
							checkedChildren={txt('global.banned')}
							unCheckedChildren={txt('global.not banned')}
						/>
					</Form.Item>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="primary"
						htmlType="submit"
						loading={updateLoading}
					>
						{txt('global.update')} <CheckOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="default"
						onClick={() => setShowWarningMailModal(true)}
					>
						{txt('user.send warning')} <WarningOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						danger
						type="ghost"
						onClick={() => setShowDeleteModal(true)}
						className="delete-button"
					>
						{txt('global.delete')} <DeleteFilled />
					</Button>
				</div>
			</Form.Item>

			<SendWarningModal
				useVisible={[showWarningMailModal, setShowWarningMailModal]}
				onConfirm={handleSendWarning}
			/>

			<DeleteModal
				onConfirm={handleDelete}
				useVisible={[showDeleteModal, setShowDeleteModal]}
				onDismiss={() => setRejectedHlDeletions([])}
			>
				{rejectedHlDeletions.length !== 0 && (
					<StyledDeleteModelContent>
						<h3>{txt('user.hl deletion error header')}</h3>
						<p>{txt('user.hl deletion error info')}</p>

						<p>
							<i>{txt('user.hl deletion error notice')}</i>
						</p>

						<ul>
							{rejectedHlDeletions.map(({ id, listId, listName }) => (
								<li key={listId}>
									<Typography.Link
										href={`https://app.heyloyalty.com/lists/${listId}/members/${id}`}
										target="_blank"
										rel="noreferrer"
										ellipsis
									>
										{listName}
									</Typography.Link>
								</li>
							))}
						</ul>
					</StyledDeleteModelContent>
				)}
			</DeleteModal>
		</StyledForm>
	);
}

const StyledDeleteModelContent = styled.div`
	margin-top: 2ch;

	h3 {
		color: ${Colors.red.primary};
	}
`;

type StyledFormProps = {
	isMobileDevice: boolean;
};

const StyledForm = styled(Form)<StyledFormProps>`
	display: flex;
	flex-direction: column;
	gap: 2em;

	.input-container {
		display: grid;
		gap: 0rem 2rem;

		${({ isMobileDevice }) => {
			if (isMobileDevice) return;
			return 'grid-template-columns: repeat(auto-fill, minmax(350px, 1fr))';
		}}
	}

	.submit-container {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};

		button.ant-switch {
			zoom: 1.4;

			span {
				font-size: 0.65em;
			}

			&.ant-switch-checked {
				background: ${Colors.red.primary};
			}
		}

		.delete-button {
			margin-left: ${({ isMobileDevice }) => (isMobileDevice ? '0' : 'auto')};
		}
	}
`;
